import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, DialogOverlayActions, Paragraph, Separator } from '@fs/zion-ui'

/**
 * Asks who should be primary in a newly created group.
 *
 * More than one primary is legal, and confirming creates one shared group rather than one each.
 *
 * @param {Object} params
 * @param {Array<{id: string, name: string}>} params.movingPersons - Everyone moving into the new group
 * @param {Array<string>} params.primaryPersonIds - Who is currently ticked
 * @param {(personId: string) => void} [params.onToggle] - Called when the patron ticks or unticks somebody
 * @returns {React.ReactElement}
 */
const ChoosePrimaryPersonStep = ({ movingPersons = [], primaryPersonIds = [], onToggle = () => {} }) => {
  const [t] = useTranslation()

  return (
    <div data-testid="choose-primary-person-step">
      <Paragraph size="sm">
        {t(
          'add-existing-person.primary-person.body',
          'Select who should be the primary person for this new group. You can select more than one.'
        )}
      </Paragraph>
      <Separator />
      {movingPersons.map((person) => (
        <Checkbox
          key={person.id}
          name={person.id}
          label={person.name}
          checked={primaryPersonIds.includes(person.id)}
          onChange={() => onToggle(person.id)}
          data-testid={`primary-person-checkbox-${person.id}`}
        />
      ))}
    </div>
  )
}

/**
 * Footer for the Choose a primary person step. Always the last step, so its primary button finishes.
 *
 * @param {Object} params
 * @param {boolean} [params.disabled=true] - Whether Create Group is disabled, i.e. nobody is ticked yet
 * @param {boolean} [params.saving=false] - Whether the save this step started is still in flight
 * @param {() => void} [params.onCancel] - Called when the patron backs out, returning them to the
 *   selection sheet with their picks intact rather than abandoning the flow
 * @param {() => void} [params.onConfirm] - Called when the patron creates the group
 * @returns {React.ReactElement}
 */
export const ChoosePrimaryPersonFooter = ({
  disabled = true,
  saving = false,
  onCancel = () => {},
  onConfirm = () => {},
}) => {
  const [t] = useTranslation()

  return (
    <DialogOverlayActions>
      <Button
        color="gray"
        disabled={saving}
        onClick={onCancel}
        linkName="Choose Primary Person Cancel"
        data-testid="choose-primary-person-cancel-button"
      >
        {t('add-existing-person.primary-person.cancel.button', 'Cancel')}
      </Button>
      <Button
        emphasis="high"
        disabled={disabled || saving}
        onClick={onConfirm}
        linkName="Choose Primary Person Create Group"
        data-testid="choose-primary-person-create-button"
      >
        {saving
          ? t('navigation.saving', 'Saving...')
          : t('add-existing-person.primary-person.create.button', 'Create Group')}
      </Button>
    </DialogOverlayActions>
  )
}

export default ChoosePrimaryPersonStep
